import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, Trash2, Bell } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { SectionHeader, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { listReminders, updateReminder, deleteReminder } from "@/lib/api";
import { formatDate, todayISO } from "@/lib/format";
import { confirmAction } from "@/lib/confirm";

const FILTERS = [
  { id: "upcoming", label: "Próximos" },
  { id: "overdue", label: "Atrasados" },
  { id: "done", label: "Concluídos" },
  { id: "all", label: "Todos" },
];

export default function Reminders() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("upcoming");

  const load = () => listReminders({}).then((all) => {
    const today = todayISO();
    let filtered = all;
    if (filter === "upcoming") filtered = all.filter((r) => !r.done && r.date >= today);
    else if (filter === "overdue") filtered = all.filter((r) => !r.done && r.date < today);
    else if (filter === "done") filtered = all.filter((r) => r.done);
    setItems(filtered);
  });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const toggle = async (r) => {
    await updateReminder(r.id, { done: !r.done });
    toast.success(r.done ? "Marcado como pendente" : "Concluído!");
    load();
  };

  const remove = async (id) => {
    if (!(await confirmAction("Remover este lembrete?"))) return;
    await deleteReminder(id);
    load();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <SectionHeader
        eyebrow="Agenda"
        title="Lembretes"
        description="Retoques, sessões e tudo o que merece atenção."
      />

      <div className="flex flex-wrap gap-2 mb-6" data-testid="reminder-filters">
        {FILTERS.map((f) => (
          <Button
            key={f.id}
            variant={filter === f.id ? "default" : "outline"}
            onClick={() => setFilter(f.id)}
            data-testid={`reminder-filter-${f.id}`}
            className={`rounded-full ${filter === f.id ? "bg-[#7B8B6F] hover:bg-[#647259] text-white" : "border-[#E8E4D9] text-[#5C6658]"}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {items.length === 0 ? (
        <Empty title="Nada por aqui" hint="Crie lembretes pelo perfil da cliente (Nova tatuagem → Lembrete de retoque)." />
      ) : (
        <div className="elegant-card divide-y divide-[#E8E4D9]" data-testid="reminders-list">
          {items.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-6 gap-4">
              <button onClick={() => toggle(r)} data-testid={`reminder-toggle-${r.id}`} className="shrink-0">
                {r.done ? <CheckCircle2 className="w-6 h-6 text-[#7B8B6F]" /> : <Circle className="w-6 h-6 text-[#5C6658]" />}
              </button>
              <div className="flex-1 min-w-0">
                <Link to={`/clientes/${r.client_id}`} className="font-serif-display text-xl text-[#2E332C] hover:text-[#7B8B6F] block truncate">
                  {r.client_name}
                </Link>
                <p className="text-xs text-[#5C6658] capitalize mt-1">{r.type} · {formatDate(r.date)}</p>
                {r.note && <p className="text-sm text-[#5C6658] italic mt-1">"{r.note}"</p>}
              </div>
              <button onClick={() => remove(r.id)} className="text-[#B86B6B] hover:text-[#8C4848]" data-testid={`reminder-delete-${r.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
